import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import type { RevenueData } from './useDashboard';

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function ChartSkeleton() {
  return (
    <div
      className="flex h-[350px] items-center justify-center rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
      role="status"
      aria-label="Loading revenue chart"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-[#e0e0e0] border-t-[#6B0F1A]" />
        <p className="text-sm text-[#666]">Loading chart...</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatter helpers
// ---------------------------------------------------------------------------

function formatCurrency(
  value: number,
  currency: string,
  options: {
    notation?: Intl.NumberFormatOptions['notation'];
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string {
  const { notation = 'standard', minimumFractionDigits, maximumFractionDigits } = options;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      notation,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(maximumFractionDigits ?? 2)}`;
  }
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface RevenueChartProps {
  data: RevenueData | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function RevenueChart({ data, isLoading, error }: RevenueChartProps) {
  const revenueByDate = data?.revenue_by_date ?? [];
  const currency = data?.currency ?? 'USD';

  if (isLoading && !data) {
    return <ChartSkeleton />;
  }

  if (error && !data) {
    return (
      <div
        className="flex h-[350px] items-center justify-center rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
        role="alert"
      >
        <p className="text-sm text-[#c62828]">Revenue data unavailable</p>
      </div>
    );
  }

  if (!data || revenueByDate.length === 0) {
    return (
      <div className="flex h-[350px] items-center justify-center rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
        <p className="text-sm text-[#999]">No revenue data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
      <h2 className="mb-4 text-lg font-bold text-[#1a1a1a]">Revenue Trend</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={revenueByDate} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fontSize: 12, fill: '#999' }}
            tickLine={false}
            axisLine={{ stroke: '#f0f0f0' }}
          />
          <YAxis
            tickFormatter={(value: number) =>
              formatCurrency(value, currency, {
                notation: value >= 1_000 ? 'compact' : 'standard',
                maximumFractionDigits: value >= 1_000_000 ? 1 : 0,
              })
            }
            tick={{ fontSize: 12, fill: '#999' }}
            tickLine={false}
            axisLine={{ stroke: '#f0f0f0' }}
            width={60}
          />
          <Tooltip
            formatter={(value: number) => [
              formatCurrency(value, currency, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              'Revenue',
            ]}
            labelFormatter={formatDate}
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          />
          <Legend wrapperStyle={{ fontSize: '13px', color: '#666' }} />
          <Line
            type="monotone"
            dataKey="amount"
            name={`Revenue (${currency})`}
            stroke="#6B0F1A"
            strokeWidth={2}
            dot={{ r: 3, fill: '#6B0F1A' }}
            activeDot={{ r: 5, stroke: '#6B0F1A', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
